import { create } from 'zustand'
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Unsubscribe
} from 'firebase/firestore'
import { getDownloadURL, ref as storageRef, uploadBytesResumable } from 'firebase/storage'
import { db, storage } from '@/config/firebase'
import { derived } from '@/lib/storeHelpers'
import { useAuthStore } from '@/stores/auth'
import { normalizeCityId } from '@/utils/cityIds'
import { resolveDisplayName } from '@/utils/displayName'
import {
  chatMuteId,
  countUnreadChats,
  fitWithin,
  reportDocId,
  resolveChatSenderRole,
  type ChatMessage,
  type ChatParticipant,
  type ChatReportReason,
  type SkateChat
} from '@/utils/skateChat'

/**
 * Skate chats (docs/SKATE_CHAT.md). Keeps the current city's chats, my
 * memberships (all cities), my private block list and, for admins, the
 * open-report count live. Conversation messages are paged by
 * `useChatMessages` in the conversation page.
 */

type Result = { success: boolean; error?: string; code?: string }

export interface ChatMute {
  uid: string
  displayName: string
  mutedByName: string
  createdAt: any
}

export interface ChatBlock {
  blockedUid: string
  blockedName: string
}

export interface ChatState {
  uid: string | null
  cityId: string | null
  isCityModerator: boolean
  chats: SkateChat[]
  chatsLoaded: boolean
  participants: Record<string, ChatParticipant>
  participantsLoaded: boolean
  blocks: Record<string, ChatBlock>
  openReportCount: number
  /** An admin has muted me from chatting in this city. */
  mutedHere: boolean
  /** Admins only: everyone muted in this city, by uid. */
  cityMutes: Record<string, ChatMute>
  // derived
  unreadCount: number
  // actions
  start: (opts: { uid: string | null; cityId: string | null; isCityModerator: boolean }) => void
  stop: () => void
  join: (chat: SkateChat) => Promise<Result>
  leave: (chatId: string) => Promise<Result>
  setMuted: (chatId: string, muted: boolean) => Promise<Result>
  markRead: (chatId: string) => Promise<void>
  sendText: (chat: SkateChat, text: string) => Promise<Result>
  sendImage: (chat: SkateChat, file: File, caption: string, onProgress?: (fraction: number) => void) => Promise<Result>
  deleteMessage: (chatId: string, messageId: string) => Promise<Result>
  block: (blockedUid: string, blockedName: string) => Promise<Result>
  unblock: (blockedUid: string) => Promise<Result>
  report: (chat: SkateChat, message: ChatMessage, reason: ChatReportReason, details: string) => Promise<Result>
  resolveReport: (report: any, resolution: 'removed' | 'dismissed') => Promise<Result>
  setLocked: (chat: SkateChat, locked: boolean) => Promise<Result>
  muteFromChatting: (cityId: string, uid: string, displayName: string) => Promise<Result>
  unmuteFromChatting: (cityId: string, uid: string) => Promise<Result>
  broadcast: (cityId: string, chatIds: string[], text: string, image?: File | null, onProgress?: (fraction: number) => void) => Promise<Result>
}

let unsubChats: Unsubscribe | null = null
let unsubParticipants: Unsubscribe | null = null
let unsubBlocks: Unsubscribe | null = null
let unsubReports: Unsubscribe | null = null
let unsubMyMute: Unsubscribe | null = null
let unsubCityMutes: Unsubscribe | null = null
let subscribedMutesKey: string | null = null
let subscribedChatsKey: string | null = null
// Chat ids whose membership the server has confirmed at least once (sticky).
const confirmedMemberships = new Set<string>()
let subscribedReportsKey: string | null = null

const errorResult = (error: any): Result => ({ success: false, error: error?.message || String(error), code: error?.code })

function myIdentity () {
  const { user, userProfile } = useAuthStore.getState()
  const name = resolveDisplayName({ name: userProfile?.name, email: user?.email, displayName: user?.displayName }) || 'Player'
  return { uid: user?.uid || null, name: name.slice(0, 80), profile: userProfile }
}

function chatFromDoc (id: string, data: any): SkateChat {
  return {
    id,
    scheduleId: data.scheduleId || id,
    cityId: data.cityId || '',
    name: data.name || 'Skate chat',
    dayOfWeek: Number(data.dayOfWeek) || 0,
    dayName: data.dayName || '',
    locked: data.locked === true,
    lockedByName: data.lockedByName || '',
    venue: data.venue || '',
    paceLabel: data.paceLabel || '',
    timeLabel: data.timeLabel || '',
    sortKey: Number(data.sortKey) || 0,
    status: data.status === 'archived' || data.status === 'inactive' ? data.status : 'active',
    participantCount: Math.max(0, Number(data.participantCount) || 0),
    lastMessage: data.lastMessage || null,
    lastMessageAt: data.lastMessageAt || null
  }
}

/** Decode with createImageBitmap, falling back to <img> where it's missing or stricter. */
async function decodeImage (file: File): Promise<{ source: CanvasImageSource; width: number; height: number; release: () => void }> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() }
  } catch {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.src = url
    try {
      await img.decode()
    } catch {
      URL.revokeObjectURL(url)
      throw new Error('That image could not be read. Try a JPEG or PNG.')
    }
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, release: () => URL.revokeObjectURL(url) }
  }
}

/** Downscale to ≤1600px on the long edge as JPEG (0.8). */
async function prepareImage (file: File): Promise<{ blob: Blob; width: number; height: number }> {
  const decoded = await decodeImage(file)
  const { width, height } = fitWithin(decoded.width, decoded.height)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  canvas.getContext('2d')!.drawImage(decoded.source, 0, 0, width, height)
  decoded.release()
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8))
  if (!blob) throw new Error('Could not process that image.')
  return { blob, width, height }
}

/** Downscale, upload with progress, and return the message `image` map. */
async function uploadChatImage (path: string, file: File, onProgress?: (fraction: number) => void) {
  const { blob, width, height } = await prepareImage(file)
  const task = uploadBytesResumable(storageRef(storage, path), blob, { contentType: 'image/jpeg' })
  await new Promise<void>((resolve, reject) => {
    task.on('state_changed', (s) => onProgress?.(s.totalBytes ? s.bytesTransferred / s.totalBytes : 0), reject, () => resolve())
  })
  const url = await getDownloadURL(task.snapshot.ref)
  return { path, url, width, height }
}

export const useChatStore = create<ChatState>()(
  derived<ChatState>((s) => ({
    unreadCount: s.cityId ? countUnreadChats(s.chats, s.participants, s.uid) : 0
  }))((set, get) => {
    const subscribeReports = (cityId: string | null, enabled: boolean) => {
      const key = enabled && cityId ? cityId : null
      if (key === subscribedReportsKey) return
      unsubReports?.()
      unsubReports = null
      subscribedReportsKey = key
      set({ openReportCount: 0 })
      if (!key) return
      unsubReports = onSnapshot(
        query(collection(db, 'chatReports'), where('cityId', '==', key), where('status', '==', 'open')),
        (snap) => set({ openReportCount: snap.size }),
        (error) => console.warn('[chat] reports listener', error)
      )
    }

    // My own mute (to explain the missing composer) and, for admins, the city's mutes.
    const subscribeMutes = (uid: string | null, city: string | null, moderator: boolean) => {
      const key = uid && city ? `${uid}|${city}|${moderator}` : null
      if (key === subscribedMutesKey) return
      subscribedMutesKey = key
      unsubMyMute?.()
      unsubCityMutes?.()
      unsubMyMute = unsubCityMutes = null
      set({ mutedHere: false, cityMutes: {} })
      if (!uid || !city) return
      unsubMyMute = onSnapshot(
        doc(db, 'chatMutes', chatMuteId(city, uid)),
        (snap) => set({ mutedHere: snap.exists() }),
        (error) => console.warn('[chat] my mute listener', error)
      )
      if (!moderator) return
      unsubCityMutes = onSnapshot(
        query(collection(db, 'chatMutes'), where('cityId', '==', city)),
        (snap) => {
          const cityMutes: Record<string, ChatMute> = {}
          for (const d of snap.docs) {
            const data = d.data()
            cityMutes[data.uid] = { uid: data.uid, displayName: data.displayName || '', mutedByName: data.mutedByName || '', createdAt: data.createdAt || null }
          }
          set({ cityMutes })
        },
        (error) => console.warn('[chat] city mutes listener', error)
      )
    }

    const start: ChatState['start'] = ({ uid, cityId, isCityModerator }) => {
      const city = cityId ? normalizeCityId(cityId) : null
      const prev = get()

      if (uid !== prev.uid) {
        unsubParticipants?.()
        unsubBlocks?.()
        unsubParticipants = null
        unsubBlocks = null
        confirmedMemberships.clear()
        set({ participants: {}, participantsLoaded: false, blocks: {} })
        if (uid) {
          // Metadata changes too: a just-joined membership is local until the server
          // confirms it, and the messages rules only pass after that (`confirmed`).
          unsubParticipants = onSnapshot(
            query(collectionGroup(db, 'participants'), where('uid', '==', uid)),
            { includeMetadataChanges: true },
            (snap) => {
              const participants: Record<string, ChatParticipant> = {}
              for (const d of snap.docs) {
                const chatId = d.ref.parent.parent?.id
                if (chatId) {
                  if (!d.metadata.hasPendingWrites) confirmedMemberships.add(chatId)
                  participants[chatId] = {
                    ...(d.data({ serverTimestamps: 'estimate' }) as ChatParticipant),
                    uid,
                    confirmed: confirmedMemberships.has(chatId)
                  }
                }
              }
              set({ participants, participantsLoaded: true })
            },
            (error) => {
              console.warn('[chat] participants listener', error)
              set({ participantsLoaded: true })
            }
          )
          unsubBlocks = onSnapshot(
            collection(db, 'users', uid, 'chatBlocks'),
            (snap) => {
              const blocks: Record<string, ChatBlock> = {}
              for (const d of snap.docs) blocks[d.id] = { blockedUid: d.id, blockedName: d.data().blockedName || '' }
              set({ blocks })
            },
            (error) => console.warn('[chat] blocks listener', error)
          )
        }
      }

      // Chats are readable only when signed in, so the listener follows both.
      const chatsKey = uid && city ? city : null
      if (chatsKey !== subscribedChatsKey) {
        unsubChats?.()
        unsubChats = null
        subscribedChatsKey = chatsKey
        set({ chats: [], chatsLoaded: false })
        if (chatsKey) {
          unsubChats = onSnapshot(
            query(collection(db, 'chats'), where('cityId', '==', chatsKey)),
            (snap) => set({ chats: snap.docs.map((d) => chatFromDoc(d.id, d.data())), chatsLoaded: true }),
            (error) => {
              console.warn('[chat] chats listener', error)
              set({ chatsLoaded: true })
            }
          )
        }
      }

      set({ uid, cityId: city, isCityModerator })
      subscribeReports(city, Boolean(uid) && isCityModerator)
      subscribeMutes(uid, city, isCityModerator)
    }

    const stop = () => {
      unsubChats?.()
      unsubParticipants?.()
      unsubBlocks?.()
      unsubChats = unsubParticipants = unsubBlocks = null
      subscribedChatsKey = null
      subscribeReports(null, false)
      subscribeMutes(null, null, false)
      set({ uid: null, cityId: null, chats: [], chatsLoaded: false, participants: {}, participantsLoaded: false, blocks: {} })
    }

    const join = async (chat: SkateChat): Promise<Result> => {
      const me = myIdentity()
      if (!me.uid) return { success: false, error: 'Sign in to join chats.' }
      try {
        await setDoc(doc(db, 'chats', chat.id, 'participants', me.uid), {
          uid: me.uid,
          cityId: chat.cityId,
          displayName: me.name,
          joinedAt: serverTimestamp(),
          muted: false,
          lastReadAt: serverTimestamp()
        })
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    const leave = async (chatId: string): Promise<Result> => {
      const { uid } = myIdentity()
      if (!uid) return { success: false }
      try {
        await deleteDoc(doc(db, 'chats', chatId, 'participants', uid))
        confirmedMemberships.delete(chatId)
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    const setMuted = async (chatId: string, muted: boolean): Promise<Result> => {
      const { uid } = myIdentity()
      if (!uid) return { success: false }
      try {
        await updateDoc(doc(db, 'chats', chatId, 'participants', uid), { muted })
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    const markRead = async (chatId: string) => {
      const { uid } = myIdentity()
      if (!uid || !get().participants[chatId]) return
      try {
        await updateDoc(doc(db, 'chats', chatId, 'participants', uid), { lastReadAt: serverTimestamp() })
      } catch (error) {
        console.warn('[chat] markRead', error)
      }
    }

    const writeMessage = async (chat: SkateChat, fields: Pick<ChatMessage, 'kind' | 'text' | 'image'>, messageRef = doc(collection(db, 'chats', chat.id, 'messages'))) => {
      const me = myIdentity()
      if (!me.uid) return { success: false, error: 'Sign in to send messages.' }
      try {
        // Resolves on server ack; the live query already shows it as pending ("Sending…").
        await setDoc(messageRef, {
          ...fields,
          senderUid: me.uid,
          senderName: me.name,
          senderRole: resolveChatSenderRole(me.profile, chat.cityId),
          createdAt: serverTimestamp(),
          deleted: false,
          deletedAt: null,
          deletedBy: null
        })
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    const sendText = async (chat: SkateChat, text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return { success: false }
      return writeMessage(chat, { kind: 'text', text: trimmed, image: null })
    }

    const sendImage = async (chat: SkateChat, file: File, caption: string, onProgress?: (fraction: number) => void) => {
      const { uid } = myIdentity()
      if (!uid) return { success: false, error: 'Sign in to send messages.' }
      try {
        const messageRef = doc(collection(db, 'chats', chat.id, 'messages'))
        const image = await uploadChatImage(`chat-images/${chat.id}/${uid}/${messageRef.id}.jpg`, file, onProgress)
        return writeMessage(chat, { kind: 'image', text: caption.trim(), image }, messageRef)
      } catch (error) {
        console.warn('[chat] sendImage', error)
        return errorResult(error)
      }
    }

    const deleteMessage = async (chatId: string, messageId: string) => {
      const { uid } = myIdentity()
      if (!uid) return { success: false }
      try {
        await updateDoc(doc(db, 'chats', chatId, 'messages', messageId), {
          deleted: true,
          deletedAt: serverTimestamp(),
          deletedBy: uid,
          text: '',
          image: null
        })
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    const block = async (blockedUid: string, blockedName: string) => {
      const { uid } = myIdentity()
      if (!uid || blockedUid === uid) return { success: false }
      try {
        await setDoc(doc(db, 'users', uid, 'chatBlocks', blockedUid), {
          blockedUid,
          blockedName: blockedName.slice(0, 80),
          createdAt: serverTimestamp()
        })
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    const unblock = async (blockedUid: string) => {
      const { uid } = myIdentity()
      if (!uid) return { success: false }
      try {
        await deleteDoc(doc(db, 'users', uid, 'chatBlocks', blockedUid))
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    const report = async (chat: SkateChat, message: ChatMessage, reason: ChatReportReason, details: string) => {
      const me = myIdentity()
      if (!me.uid) return { success: false }
      const reportRef = doc(db, 'chatReports', reportDocId(message.id, me.uid))
      try {
        await setDoc(reportRef, {
          chatId: chat.id,
          chatName: chat.name.slice(0, 200),
          cityId: chat.cityId,
          messageId: message.id,
          reporterUid: me.uid,
          reporterName: me.name,
          reason,
          details: details.trim().slice(0, 500),
          message: {
            text: message.text,
            senderUid: message.senderUid,
            senderName: message.senderName,
            senderRole: message.senderRole,
            imageUrl: message.image?.url || null
          },
          status: 'open',
          createdAt: serverTimestamp()
        })
        return { success: true }
      } catch (error: any) {
        // The id is deterministic, so a second report is a denied overwrite.
        if (error?.code === 'permission-denied') {
          const existing = await getDoc(reportRef).catch(() => null)
          if (existing?.exists()) return { success: false, code: 'already-reported' }
        }
        return errorResult(error)
      }
    }

    const resolveReport = async (reportRow: any, resolution: 'removed' | 'dismissed') => {
      const me = myIdentity()
      if (!me.uid) return { success: false }
      try {
        if (resolution === 'removed') {
          const messageRef = doc(db, 'chats', reportRow.chatId, 'messages', reportRow.messageId)
          const current = await getDoc(messageRef)
          if (current.exists() && !current.data().deleted) {
            const removed = await deleteMessage(reportRow.chatId, reportRow.messageId)
            if (!removed.success) return removed
          }
        }
        await updateDoc(doc(db, 'chatReports', reportRow.id), {
          status: resolution === 'removed' ? 'resolved' : 'dismissed',
          resolution,
          resolvedBy: me.uid,
          resolvedByName: me.name,
          resolvedAt: serverTimestamp()
        })
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    const setLocked = async (chat: SkateChat, locked: boolean) => {
      const me = myIdentity()
      if (!me.uid) return { success: false }
      try {
        await updateDoc(doc(db, 'chats', chat.id), {
          locked,
          lockedBy: me.uid,
          lockedByName: me.name,
          lockedAt: serverTimestamp()
        })
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    const muteFromChatting = async (cityId: string, uid: string, displayName: string) => {
      const me = myIdentity()
      if (!me.uid) return { success: false }
      const city = normalizeCityId(cityId)
      try {
        await setDoc(doc(db, 'chatMutes', chatMuteId(city, uid)), {
          cityId: city,
          uid,
          displayName: displayName.slice(0, 80),
          mutedBy: me.uid,
          mutedByName: me.name,
          createdAt: serverTimestamp()
        })
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    const unmuteFromChatting = async (cityId: string, uid: string) => {
      try {
        await deleteDoc(doc(db, 'chatMutes', chatMuteId(normalizeCityId(cityId), uid)))
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    // Super Admins: the onChatBroadcastCreated trigger copies it into every active chat in the city.
    const broadcast = async (cityId: string, chatIds: string[], text: string, imageFile?: File | null, onProgress?: (fraction: number) => void) => {
      const me = myIdentity()
      const trimmed = text.trim()
      if (!me.uid || (!trimmed && !imageFile) || chatIds.length === 0) return { success: false }
      try {
        const broadcastRef = doc(collection(db, 'chatBroadcasts'))
        // One shared file for every chat's copy.
        const image = imageFile
          ? await uploadChatImage(`chat-broadcasts/${me.uid}/${broadcastRef.id}.jpg`, imageFile, onProgress)
          : null
        await setDoc(broadcastRef, {
          cityId: normalizeCityId(cityId),
          chatIds,
          text: trimmed,
          ...(image ? { image } : {}),
          senderUid: me.uid,
          senderName: me.name,
          status: 'pending',
          createdAt: serverTimestamp()
        })
        return { success: true }
      } catch (error) {
        return errorResult(error)
      }
    }

    return {
      uid: null,
      cityId: null,
      isCityModerator: false,
      chats: [],
      chatsLoaded: false,
      participants: {},
      participantsLoaded: false,
      blocks: {},
      openReportCount: 0,
      mutedHere: false,
      cityMutes: {},
      unreadCount: 0,
      start,
      stop,
      join,
      leave,
      setMuted,
      markRead,
      sendText,
      sendImage,
      deleteMessage,
      block,
      unblock,
      report,
      resolveReport,
      setLocked,
      muteFromChatting,
      unmuteFromChatting,
      broadcast
    }
  })
)
